import type {
  FieldErrors,
  FormNotice,
  LoginValues,
  PasswordPolicy,
  SignupValues,
} from "./authFormState.js";

interface BaseFormModel {
  readonly busy: boolean;
  readonly submitDisabled?: boolean;
  readonly submitLabel?: string;
  readonly fieldErrors?: FieldErrors;
  readonly notice?: FormNotice;
  readonly onNoticeAction?: () => void;
}

export interface LoginScreenModel extends BaseFormModel {
  readonly kind: "login";
  readonly showSignup: boolean;
  readonly signupHref: string;
  readonly values?: Partial<LoginValues>;
  readonly onSubmit: (values: LoginValues, form: HTMLFormElement) => void;
}

export interface SignupScreenModel extends BaseFormModel {
  readonly kind: "signup";
  readonly policy: PasswordPolicy;
  readonly loginHref: string;
  readonly values?: Partial<SignupValues>;
  readonly onSubmit: (values: SignupValues, form: HTMLFormElement) => void;
}

export interface MessageAction {
  readonly label: string;
  readonly onActivate: () => void;
  readonly secondary?: boolean;
  readonly autofocus?: boolean;
}

export interface MessageScreenModel {
  readonly kind: "message";
  readonly status: string;
  readonly title: string;
  readonly description: string;
  readonly glyph?: string;
  readonly loading?: boolean;
  readonly actions?: readonly MessageAction[];
}

export type AuthScreenModel =
  | LoginScreenModel
  | SignupScreenModel
  | MessageScreenModel;

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function appendHeader(root: HTMLElement, status: string): void {
  const header = element("header", "gc-auth-header");
  const logo = element("div", "gc-auth-logo");
  logo.setAttribute("aria-label", "GoCanvas");
  logo.append("Go", element("b", undefined, "Canvas"));
  const session = element("div", "gc-auth-session", status);
  session.setAttribute("role", "status");
  header.append(logo, session);
  root.append(header);
}

function describedBy(...ids: Array<string | undefined>): string | undefined {
  const value = ids.filter(Boolean).join(" ");
  return value || undefined;
}

function appendField(
  form: HTMLFormElement,
  options: {
    readonly prefix: string;
    readonly name: string;
    readonly label: string;
    readonly type: string;
    readonly autocomplete: string;
    readonly value?: string | undefined;
    readonly busy: boolean;
    readonly error?: string | undefined;
    readonly hint?: string | undefined;
  },
): void {
  const wrapper = element("div", "gc-auth-field");
  const id = `${options.prefix}-${options.name}`;
  const label = element("label", undefined, options.label);
  label.htmlFor = id;
  const input = element("input");
  input.id = id;
  input.name = options.name;
  input.type = options.type;
  input.setAttribute("autocomplete", options.autocomplete);
  input.required = true;
  input.disabled = options.busy;
  input.value = options.value ?? "";
  if (options.type === "email") {
    input.inputMode = "email";
    input.spellcheck = false;
    input.autocapitalize = "none";
  }

  const errorId = options.error ? `${id}-error` : undefined;
  const hintId = options.hint ? `${id}-message` : undefined;
  const description = describedBy(errorId, hintId);
  if (description) input.setAttribute("aria-describedby", description);
  if (options.error) input.setAttribute("aria-invalid", "true");
  wrapper.append(label, input);
  if (options.error) {
    const error = element("p", "gc-auth-field-error", options.error);
    error.id = errorId!;
    wrapper.append(error);
  }
  if (options.hint) {
    const hint = element("p", "gc-auth-policy", options.hint);
    hint.id = hintId!;
    wrapper.append(hint);
  }
  form.append(wrapper);
}

function appendNotice(
  card: HTMLElement,
  notice: FormNotice,
  onAction?: () => void,
): HTMLElement {
  const summary = element("div", "gc-auth-alert");
  summary.dataset.authSummary = "";
  summary.setAttribute("role", "alert");
  summary.setAttribute("aria-live", "assertive");
  summary.tabIndex = -1;
  summary.append(
    element("strong", undefined, notice.title),
    document.createTextNode(notice.message),
  );
  card.append(summary);
  if (notice.action && onAction) {
    const action = element("button", "gc-auth-notice-action", notice.action);
    action.type = "button";
    action.addEventListener("click", onAction);
    card.append(action);
  }
  return summary;
}

function appendSubmit(
  form: HTMLFormElement,
  busy: boolean,
  disabled: boolean,
  label: string,
): void {
  const actions = element("div", "gc-auth-actions");
  const submit = element("button", "gc-auth-button", label);
  submit.type = "submit";
  submit.disabled = busy || disabled;
  if (busy) submit.setAttribute("aria-busy", "true");
  actions.append(submit);
  form.append(actions);
}

function formShell(
  status: string,
  title: string,
  description: string,
): { root: HTMLElement; main: HTMLElement; card: HTMLElement } {
  const root = element("div", "gc-auth-shell");
  root.dataset.testid = "auth-screen-root";
  appendHeader(root, status);
  const main = element("main", "gc-auth-main");
  const card = element("section", "gc-auth-card");
  card.setAttribute("aria-labelledby", "auth-title");
  const heading = element("h1", undefined, title);
  heading.id = "auth-title";
  card.append(heading, element("p", undefined, description));
  main.append(card);
  root.append(main);
  return { root, main, card };
}

function renderLogin(model: LoginScreenModel): HTMLElement {
  const { root, card } = formShell(
    model.busy ? "Выполняется вход" : "Готово ко входу",
    "Войти в GoCanvas",
    model.busy
      ? "Проверяем данные на сервере."
      : "Используйте email и пароль вашей учётной записи.",
  );
  if (model.notice)
    appendNotice(card, model.notice, model.onNoticeAction);
  const form = element("form", "gc-auth-form");
  form.noValidate = true;
  if (model.busy) form.setAttribute("aria-busy", "true");
  appendField(form, {
    prefix: "login",
    name: "email",
    label: "Email",
    type: "email",
    autocomplete: "email",
    value: model.values?.email,
    busy: model.busy,
    error: model.fieldErrors?.email,
  });
  appendField(form, {
    prefix: "login",
    name: "password",
    label: "Пароль",
    type: "password",
    autocomplete: "current-password",
    busy: model.busy,
    error: model.fieldErrors?.password,
  });
  appendSubmit(
    form,
    model.busy,
    model.submitDisabled ?? false,
    model.submitLabel ?? (model.busy ? "Входим…" : "Войти"),
  );
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const data = new FormData(form);
    model.onSubmit(
      {
        email: String(data.get("email") ?? ""),
        password: String(data.get("password") ?? ""),
      },
      form,
    );
  });
  card.append(form);
  if (model.showSignup) {
    const footer = element("div", "gc-auth-footer", "Нет аккаунта? ");
    const link = element("a", "gc-auth-link", "Регистрация");
    link.href = model.signupHref;
    footer.append(link);
    card.append(footer);
  }
  return root;
}

function renderSignup(model: SignupScreenModel): HTMLElement {
  const { root, card } = formShell(
    model.busy ? "Создание аккаунта" : "Регистрация доступна",
    "Создать аккаунт",
    model.busy
      ? "Создаём учётную запись на сервере."
      : "Создайте локальную учётную запись GoCanvas.",
  );
  if (model.notice)
    appendNotice(card, model.notice, model.onNoticeAction);
  const form = element("form", "gc-auth-form");
  form.noValidate = true;
  if (model.busy) form.setAttribute("aria-busy", "true");
  appendField(form, {
    prefix: "signup",
    name: "displayName",
    label: "Имя",
    type: "text",
    autocomplete: "name",
    value: model.values?.displayName,
    busy: model.busy,
    error: model.fieldErrors?.displayName,
  });
  appendField(form, {
    prefix: "signup",
    name: "email",
    label: "Email",
    type: "email",
    autocomplete: "email",
    value: model.values?.email,
    busy: model.busy,
    error: model.fieldErrors?.email,
  });
  appendField(form, {
    prefix: "signup",
    name: "password",
    label: "Пароль",
    type: "password",
    autocomplete: "new-password",
    busy: model.busy,
    error: model.fieldErrors?.password,
    hint: `Требования к паролю: от ${model.policy.minLength} до ${model.policy.maxLength} символов.`,
  });
  appendSubmit(
    form,
    model.busy,
    model.submitDisabled ?? false,
    model.submitLabel ??
      (model.busy ? "Создаём аккаунт…" : "Создать аккаунт"),
  );
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const data = new FormData(form);
    model.onSubmit(
      {
        displayName: String(data.get("displayName") ?? ""),
        email: String(data.get("email") ?? ""),
        password: String(data.get("password") ?? ""),
      },
      form,
    );
  });
  card.append(form);
  const footer = element("div", "gc-auth-footer", "Уже есть аккаунт? ");
  const link = element("a", "gc-auth-link", "Войти");
  link.href = model.loginHref;
  footer.append(link);
  card.append(footer);
  return root;
}

function renderMessage(model: MessageScreenModel): HTMLElement {
  const { root, card } = formShell(
    model.status,
    model.title,
    model.description,
  );
  const glyph = element(
    "div",
    `gc-auth-glyph${model.loading ? " gc-auth-spinner" : ""}`,
    model.loading ? undefined : (model.glyph ?? "!"),
  );
  glyph.setAttribute("aria-hidden", "true");
  card.insertBefore(glyph, card.firstChild);
  if (model.actions?.length) {
    const actions = element("div", "gc-auth-actions");
    for (const action of model.actions) {
      const button = element("button", "gc-auth-button", action.label);
      button.type = "button";
      if (action.secondary) button.dataset.variant = "secondary";
      button.addEventListener("click", action.onActivate);
      actions.append(button);
      if (action.autofocus) queueMicrotask(() => button.focus());
    }
    card.append(actions);
  }
  return root;
}

export function renderAuthScreen(model: AuthScreenModel): HTMLElement {
  switch (model.kind) {
    case "login":
      return renderLogin(model);
    case "signup":
      return renderSignup(model);
    case "message":
      return renderMessage(model);
  }
}

export function focusAuthError(screen: ParentNode): void {
  const summary = screen.querySelector<HTMLElement>("[data-auth-summary]");
  if (summary) {
    summary.focus();
    return;
  }
  screen
    .querySelector<HTMLInputElement>('[aria-invalid="true"]')
    ?.focus();
}

export function focusFirstInvalid(screen: ParentNode): void {
  screen
    .querySelector<HTMLInputElement>('[aria-invalid="true"]')
    ?.focus();
}
